import {
  nonEmptyStringOrNull,
  getPotContactId,
  updateRecsVarsProps,
  upsertBuilderHeader,
  validateMutationResponse,
} from './builderHelpers';
import {
  OVRecommendActionEnum,
  RecurringFrequencyEnum,
} from '../../../schema-types/globalTypes';
import validateUUID from '../../../utils/validateUUID';
import {getISODateOrUndefined} from '../../../utils/getISODate';
import FREQUENCY from '../../../constants/Frequency';

// FREQUENCY (Title Case, e.g. 'Monthly') -> RecurringFrequencyEnum (ALL CAPS) the
// withdrawal mutation expects. The two enums share keys but differ in value.
const FREQUENCY_TO_RECURRING: Record<FREQUENCY, RecurringFrequencyEnum> = {
  [FREQUENCY.SINGLE]: RecurringFrequencyEnum.SINGLE,
  [FREQUENCY.MONTHLY]: RecurringFrequencyEnum.MONTHLY,
  [FREQUENCY.QUARTERLY]: RecurringFrequencyEnum.QUARTERLY,
  [FREQUENCY.ANNUAL]: RecurringFrequencyEnum.ANNUAL,
};

export const saveWithdrawal = async (props: updateRecsVarsProps): Promise<string> => {
  const {accountId, values, builderDetails} = props;
  const {withdraw, contact, urgency, urgencyReason} = values;

  // Destinations are existing BankAccount entities (DBO-1436); new bank
  // accounts are created up front, never as part of the withdrawal.
  const destinationBankAccountId = withdraw?.destination
    ? validateUUID(withdraw.destination.id)
    : null;
  if (!withdraw || !destinationBankAccountId) {
    throw Error('Must have a destination bank account');
  }

  const ovrecommendId = await upsertBuilderHeader(props, {
    action: OVRecommendActionEnum.WITHDRAW,
    accountId: validateUUID(accountId),
    goalId: validateUUID(values.goal?.id),
    // Always false: destinations are existing bank accounts, and sending it
    // clears the flag on recs saved under the old new-bank flow.
    isNewPlanRequired: false,
    contactId: getPotContactId(contact),
    withdrawalReason: nonEmptyStringOrNull(
      withdraw?.source?.reason || builderDetails?.withdrawalReason
    ),
    urgency,
    urgencyReason,
    startDate:
      getISODateOrUndefined(withdraw?.source?.start) ||
      getISODateOrUndefined(builderDetails?.startDate),
    endDate:
      getISODateOrUndefined(withdraw?.source?.end) ||
      getISODateOrUndefined(builderDetails?.endDate),
  });

  const withdrawalFrequency =
    FREQUENCY_TO_RECURRING[withdraw.source.frequency] ?? RecurringFrequencyEnum.SINGLE;
  const isRecurring = withdrawalFrequency !== RecurringFrequencyEnum.SINGLE;

  const withdrawResult = await props.upsertBuilderWithdraw({
    variables: {
      ovrecommendId: validateUUID(ovrecommendId)!,
      isFull: Boolean(withdraw.source.full),
      sourcePotId: withdraw.source.id,
      destinationBankAccountId,
      withdrawalFrequency,
      withdrawalAmount:
        Number(
          isRecurring
            ? withdraw.source.recurringClientAmount
            : withdraw.source.singleClientAmount
        ) || 0,
    },
  });

  validateMutationResponse(
    withdrawResult.data?.upsertWithdraw,
    'Failed to save withdrawal'
  );

  return ovrecommendId;
};
